import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Avatar } from 'src/components/Avatar';
import { CircularProgress } from 'src/components/CircularProgress';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { Theme } from 'src/components/muiTheme/utils';
import { SxProps } from 'src/components/muiTheme/utils';
import { searchGroupMembers } from 'src/models/group/group';

interface ExerciseAutocompleteProps {
  value: any;
  onChange: (val: any) => void;
  label?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  onEnter?: (value: string) => void;
  // clear options when input is cleared
  clearOptions?: boolean;
  types: string[];
}

export const GroupMemberAutocomplete: React.FC<ExerciseAutocompleteProps> = ({
  value,
  onChange,
  label,
  placeholder = '',
  sx,
  onEnter,
  clearOptions = false,
  types,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'group.member' });
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<any[]>([]);

  const {
    data: _groupMembers,
    refetch: refetchMembers,
    isFetching,
  } = useQuery({
    queryKey: ['groupMembers', 'search', searchValue],

    queryFn: ({ signal }) =>
      searchGroupMembers({
        search: searchValue,
        types,
        signal,
      }),

    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  const debouncedSearch = debounce(async (value: string) => {
    if (value.length >= 2) {
      // if conflict with GroupMemberAutocomplete, use this
      const { data } = await refetchMembers();
      setOptions(data || []);
    }
  }, 0);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string
  ) => {
    if (reason === 'clear' && clearOptions) {
      setSearchValue('');
      setOptions([]);
      return;
    }
    if (reason === 'input') {
      setSearchValue(value);

      if (!value && clearOptions) {
        // clear options when input is cleared
        setOptions([]);
        return;
      }

      debouncedSearch(value);
    }
  };

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.name
      }
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      loading={isFetching}
      filterOptions={(x) => x}
      noOptionsText={searchValue ? t('noResult') : t('pleaseEnter')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder || t('placeholder')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size="small" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
            onKeyUp: (e) => {
              if (e.key === 'Enter') {
                onEnter?.(searchValue);
              }
            },
            onBlur: () => {
              if (clearOptions && !value) {
                setSearchValue('');
                setOptions([]);
              }
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <ListItem
            {...(props as any)}
            key={option.id}
            clickable
            primary={option.name}
            avatar={<Avatar src={option.image} />}
          />
        );
      }}
      sx={sx}
    />
  );
};
